import { useCallback, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { cloneDeep, isEmpty, isNaN, omit } from 'lodash';

import { usePageStateValue } from '../../stores/PageStateStore';
import { DataTablePropTypes } from './DataTableProps';
import DataTable from './DataTable';
import { useStickyState } from './TableState';
import useIsPrinting, { getPrintRowOverride } from '../../utilities/PrintUtilities';
import { useId, useOptions, useIsAutoHeight } from '../page-framework/panel/Panel';
import { useDebounced, useIsScheduledReport, useThrottled } from '../../utilities/Hooks';
import { useFilterControl } from '../page-framework/filter/FilterControl';
import { useDashboardContext, isDashboard } from '../page-framework/dashboard/DashboardContext';
import { usePageId } from '../page-framework/page/PageUtils';

const DELIMETER = '/';

export default function ReportingDataTable(props) {
    const { panelProps, tableId, ...other } = props;
    const isPrinting = useIsPrinting();
    const filterControl = useFilterControl();
    const { editing } = useDashboardContext();
    const id = useId();
    const [options, setOptions] = useOptions();
    // We need to debounce setting options to avoid a state change feedback loop when resizing columns
    // during dashboard editing.
    const setOptionsDebounced = useDebounced(setOptions, 15);
    const parentVisibleRows = useVisibleRows(panelProps);
    // pageStateKey overridden when rendering the path analysis list on the test log page
    const pageStateKey = props.pageStateKey || `${id}-TableState`;
    const [encodedTablePageState, tablePageStateActions] = usePageStateValue({
        key: pageStateKey,
        defaultValue: null,
    });
    const setPageStateValue = useRef();
    const clearPageStateValue = useRef();
    const enableStickyState = !isDashboard(usePageId());
    const [stickyTableState, setStickyTableState] = useStickyState(tableId || id, enableStickyState);

    setPageStateValue.current = useThrottled(tablePageStateActions.setValue, 1000);
    clearPageStateValue.current = () => {
        // Only clear page state if editing a dashboard.
        if (editing) tablePageStateActions.clearValue(pageStateKey);
    };
    const handleStateChange = useCallback(
        (newTableState) => {
            // Only update the page state when not editing.
            if (!editing) {
                setPageStateValue.current(pageStateKey, encodeState(newTableState));
                setStickyTableState(newTableState);
            } else {
                // Update the panel options with the new table state.
                const searchOptions = isEmpty(newTableState.search)
                    ? omit(options, ['stickySearch', 'tableConfig'])
                    : {
                          ...omit(options, ['tableConfig']),
                          stickySearch: newTableState.search,
                      };
                setOptionsDebounced({
                    ...searchOptions,
                    columnOverrides: newTableState.columnOverrides,
                    sorters: newTableState.sorters,
                });
            }
        },
        [editing, pageStateKey, options, setOptionsDebounced, setStickyTableState]
    );

    // Clear the page state value when the table is unmounted.  This keeps the page
    // state clean when editing custom dashboards.
    useEffect(() => {
        return () => clearPageStateValue.current();
    }, []);

    const { visibleRows: tableVisibleRows, state: tableState } = useConfigOverrides(
        encodedTablePageState,
        stickyTableState,
        parentVisibleRows,
        props?.columnOverrides || [],
        props?.sorters || [],
        id
    );

    const onScroll = useCallback(
        (scrollTop) => {
            if (!filterControl) return;

            if (scrollTop > 0) {
                filterControl.pauseAutoRefresh();
            } else {
                filterControl.resumeAutoRefresh();
            }
        },
        [filterControl]
    );

    // Don't pass `visibleRows` when printing.  The `visibleRows` prop overrides the
    // row behavior of the data table, and prevents the data table from rendering the
    // appropriate number of rows when printing.
    const tableProps = isPrinting ? omit(other, 'visibleRows') : other;

    return (
        <DataTable
            tableId={tableId || id}
            editing={editing}
            state={tableState}
            visibleRows={tableVisibleRows}
            onStateChanged={handleStateChange}
            enableStickyState={enableStickyState}
            onScroll={onScroll}
            {...tableProps}
        />
    );
}

ReportingDataTable.propTypes = {
    panelProps: PropTypes.shape({ id: PropTypes.string }).isRequired,

    ...DataTablePropTypes,
};

// rowStart/rowEnd/search/sortDir/sortStr/columnOverrides
function encodeState(tableState) {
    if (!tableState) {
        return null;
    }

    const { rowBounds, search, sorters } = tableState;

    const [firstIndex, lastIndex] = rowBounds || [0, 1];
    const sortDirections = JSON.stringify(sorters?.map((sorter) => sorter.direction) || []);
    const sortProperties = JSON.stringify(sorters?.map((sorter) => sorter.property) || []);

    const values = [firstIndex, lastIndex, search || '', sortDirections, sortProperties];

    return values.map(encodeURIComponent).join(DELIMETER);
}

function decodeState(tableStateStr) {
    if (!tableStateStr || typeof tableStateStr !== 'string') {
        return null;
    }

    const state = {};
    const values = tableStateStr.split(DELIMETER).map(decodeURIComponent);

    // bad string, skip it
    if (values.length !== 5) {
        return null;
    }

    const [firstIndexStr, lastIndexStr, search, sortDirectionsStr, sortPropertiesStr] = values;
    const sortDirections = JSON.parse(sortDirectionsStr);
    const sortProperties = JSON.parse(sortPropertiesStr);

    state.search = search;

    const firstIndex = parseInt(firstIndexStr, 10);
    const lastIndex = parseInt(lastIndexStr, 10);

    if (!isNaN(firstIndex) && !isNaN(lastIndex) && lastIndex >= firstIndex && firstIndex >= 0) {
        state.rowBounds = [firstIndex, lastIndex];
    }

    if (sortDirections?.length > 0 && sortProperties?.length > 0 && sortDirections?.length === sortProperties?.length) {
        state.sorters = sortDirections.map((direction, index) => ({
            direction,
            property: sortProperties[index],
        }));
    }

    return state;
}

/**
 * A "reporting" datatable can operate in several different modes depending on if we are printing
 * or not. We will override rowBounds and/or visibleRows depending on these modes.
 *
 * Those three modes are:
 *
 * ### UI Mode
 *
 * This mode does not override any of the provided configurations/props. This is what we see when
 * accessing nGP in the browser. `rowBounds` will be unchanged if coming from state and
 * `visibleRows` will be whatever is provided via the props.
 *
 * ### Automatic Row Mode (Printing Only)
 *
 * This mode is used when the user does not specify a number of rows to see in their report. The
 * placeholder text in that input shows "Auto". This means the datatable must reproduce exactly
 * what the user sees on their screen. In this case, we override visibleRows so the table is the
 * correct height.
 *
 * This configuration requires that `rowBounds` is provided (usually from pageState). If this is
 * not available, that's an error (do not default this value). `rowBounds` is used to keep track
 * of the first and last row that the user currently has visible in their datatable.
 *
 * Overrides:
 * - visibleRows: `visibleRows = lastRowIndexFromRowBounds - firstRowIndexFromRowBounds`
 *
 * ### Override Row Mode (Printing Only)
 *
 * This mode is used when the user specifies the number of rows they wish to see in their report.
 * This will override the number of rows they see. For ad-hoc reports (download/email) this will
 * keep the scroll position, so if the user is scrolled 10 rows down and requests 100 rows, they'll
 * see rows 10-110. However, scheduled reports do not persist scroll position and reset it to zero.
 * The table will always start at row 0 for scheduled reports.
 *
 * Overrides:
 * - visibleRows: `visibleRows = userRows`. That value comes from session storage.
 * - rowBounds: `rowBounds = [firstRowIndexFromRowBounds, firstRowIndexFromRowBounds + userRows]`.
 *      Note that this will be `[0, userRows]` for scheduled reports.
 */
function useConfigOverrides(
    encodedTablePageState,
    stickyTableState,
    parentVisibleRows,
    initialColumnOverrides,
    initialSorters,
    id
) {
    const { editing, saving } = useDashboardContext();
    const optionsTableState = useOptionsTableState();
    const pageTableState = useRef(decodeState(encodedTablePageState));
    const stickyTableStateRef = useRef();

    const isPrinting = useIsPrinting();
    const rowOverride = getPrintRowOverride();
    const isAutoRowMode = isPrinting && rowOverride === null;
    const isOverrideRowMode = isPrinting && rowOverride !== null;

    const updatePageTableState = useRef();
    const isScheduledReport = useIsScheduledReport();

    stickyTableStateRef.current = stickyTableState;

    updatePageTableState.current = () => {
        // If we're not editing, we want keep the pageTableState up to date.  This
        // gives us a state to restore to if the user edits and then cancels.
        if (!editing) pageTableState.current = decodeState(encodedTablePageState);
    };

    useEffect(() => {
        updatePageTableState.current();
    }, [encodedTablePageState]);

    useEffect(() => {
        // When a dashboard is being saved, we want the options state that
        // is being saved to replace the page state.
        if (saving) pageTableState.current = cloneDeep(optionsTableState);
    }, [saving, optionsTableState]);

    const state = useMemo(() => {
        // When editing, we always want to use the options state, since the user will be
        // editing the saved options.  When not editing, we use the options state as the
        // default, but it will be overridden by the page state if present.
        return editing
            ? optionsTableState
            : {
                  ...optionsTableState,
                  ...(stickyTableStateRef.current || {}),
                  ...(pageTableState.current || {}),
              };
    }, [editing, optionsTableState]);

    let visibleRows;

    // If we're not printing, we're in "Normal UI" mode...
    if (!isPrinting) {
        visibleRows = parentVisibleRows;
    } else if (isOverrideRowMode) {
        let [startRow] = state.rowBounds || [0, 1];
        visibleRows = rowOverride;

        // note: scheduled reports start at row 0, not the row the user was on.
        if (isScheduledReport) startRow = 0;

        state.rowBounds = [startRow, startRow + visibleRows];
    } else if (isAutoRowMode) {
        if (state.rowBounds?.length !== 2) {
            // See method comment. This means the user wanted a report to match the number of rows they saw, but the state provided
            // for this table did not contain rowBounds. This is an errornous state and means a dev likely missed something
            throw new Error(
                `DataTable[id="${id}"]. "Auto" rows requested (match user screen), but we did not get rowBounds. State: "${JSON.stringify(
                    state
                )}"`
            );
        }

        const [startRow, endRow] = state.rowBounds;
        visibleRows = endRow - startRow;
    }

    if (!state?.columnOverrides) {
        state.columnOverrides = [...initialColumnOverrides];
    }

    if (!state?.sorters) {
        state.sorters = [...initialSorters];
    }

    return {
        visibleRows,
        state,
    };
}

function useVisibleRows() {
    const isAutoHeight = useIsAutoHeight();
    const [options] = useOptions();
    const visibleRows = useMemo(() => {
        if (!isAutoHeight) return 0;
        return options?.tableConfig?.visibleRows || 5;
    }, [options, isAutoHeight]);

    return visibleRows;
}

function useOptionsTableState() {
    const [options] = useOptions();
    return useMemo(
        () => ({
            search: options?.stickySearch || options?.tableConfig?.stickySearch || '',
            columnOverrides: options?.columnOverrides,
            sorters: options?.sorters,
        }),
        [options]
    );
}
